import type { NamedHierarchyNode } from "@contract-manager/shared";
import { type ReactNode, useCallback, useEffect, useState } from "react";
import { useAuth } from "../auth/AuthContext";
import { describeError, isSignedOutError } from "../auth/authErrors";
import { useI18n } from "../i18n/I18n";
import { Alert } from "./Alert";
import { ConfirmDialog } from "./Dialog";
import { HierarchyTree } from "./HierarchyTree";
import { MoveDialog, RenameDialog } from "./NodeDialogs";

/**
 * The half of the folder and team screens that is genuinely the same: load the
 * nodes, draw the tree, create, rename, move and delete.
 *
 * What differs — what a selected node contains, and what you can do with its
 * contents — is passed in as `children`. Writing the two screens separately
 * would mean two copies of the reload-after-every-change logic, which is where
 * this kind of page actually goes wrong.
 */

export type HierarchyKind = "folder" | "team";

export interface NodeOperations {
  /** Which tree this is; every label in the panel is chosen by it. */
  readonly kind: HierarchyKind;
  list(): Promise<NamedHierarchyNode[]>;
  /** Shown after a node's name, for nodes that are not like the others. */
  badge?(id: string): ReactNode;
  create(input: { name: string; parentId: string | null }): Promise<unknown>;
  update(
    id: string,
    changes: { name?: string; parentId?: string | null },
  ): Promise<unknown>;
  remove(id: string): Promise<unknown>;
}

/** Which node action is waiting for an answer in a dialog. */
type PendingAction = {
  readonly kind: "rename" | "move" | "remove";
  readonly node: NamedHierarchyNode;
};

export function HierarchyAdmin({
  operations,
  canManage,
  selectedId,
  onSelectedChange,
  children,
}: {
  operations: NodeOperations;
  /**
   * Whether to offer creating, renaming, moving and deleting. Reshaping the
   * tree is for administrators; everyone else gets the tree to browse.
   */
  canManage: boolean;
  selectedId: string | null;
  onSelectedChange: (id: string | null) => void;
  /** The detail pane for the selected node. */
  children: (node: NamedHierarchyNode) => ReactNode;
}) {
  const auth = useAuth();
  const { t } = useI18n();
  const { kind } = operations;
  const [nodes, setNodes] = useState<NamedHierarchyNode[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [ready, setReady] = useState(false);
  const [newName, setNewName] = useState("");
  const [busy, setBusy] = useState(false);
  const [pending, setPending] = useState<PendingAction | null>(null);

  // Only `list` decides when to fetch. A caller may rebuild the rest of
  // `operations` — `badge` in particular — as what it learns from a fetch
  // changes, and that must not start another one.
  const { list } = operations;
  const load = useCallback(async () => {
    try {
      setNodes(await list());
      setError(null);
    } catch (caught) {
      if (isSignedOutError(caught)) {
        auth.reportSignedOut();
        return;
      }
      setError(describeError(caught, t));
    } finally {
      setReady(true);
    }
  }, [auth, list, t]);

  useEffect(() => {
    void load();
  }, [load]);

  /**
   * Every mutation reloads the whole set rather than patching local state.
   *
   * The tree is small, and the alternative — keeping a client-side copy in
   * step with moves and deletes someone else may also be making — is exactly
   * the bug this screen would be reported for.
   */
  const run = useCallback(
    async (action: () => Promise<unknown>) => {
      setBusy(true);
      setError(null);
      try {
        await action();
        await load();
        return true;
      } catch (caught) {
        if (isSignedOutError(caught)) {
          auth.reportSignedOut();
          return false;
        }
        setError(describeError(caught, t));
        return false;
      } finally {
        setBusy(false);
      }
    },
    [auth, load, t],
  );

  const selected = nodes.find((node) => node.id === selectedId) ?? null;

  const create = async () => {
    const name = newName.trim();
    if (name === "") {
      return;
    }
    // New nodes land under whatever is selected, which is what makes building
    // a tree a sequence of clicks rather than a sequence of moves.
    //
    // `selected`, not `selectedId`: if another session deleted the selected
    // node, the reload has already dropped it from `nodes`, and creating under
    // the id that is no longer there would 404 instead of making a root.
    const created = await run(() =>
      operations.create({ name, parentId: selected?.id ?? null }),
    );
    if (created) {
      setNewName("");
    }
  };

  // Each dialog closes before its request goes out, as the browser prompts
  // did; a failure lands in the panel's alert like every other one.
  const rename = async (node: NamedHierarchyNode, name: string) => {
    setPending(null);
    await run(() => operations.update(node.id, { name }));
  };

  const move = async (node: NamedHierarchyNode, parentId: string | null) => {
    setPending(null);
    await run(() => operations.update(node.id, { parentId }));
  };

  const remove = async (node: NamedHierarchyNode) => {
    setPending(null);
    const removed = await run(() => operations.remove(node.id));
    if (removed && node.id === selectedId) {
      onSelectedChange(null);
    }
  };

  const cancel = () => setPending(null);

  return (
    <div className="app-master-detail">
      <section className="ds-card">
        <h2 className="ds-text --large">{t(`organisation.${kind}.heading`)}</h2>

        {canManage && (
          <form
            onSubmit={(event) => {
              event.preventDefault();
              void create();
            }}
          >
            {/* SDS makes any form inside ds-rows a rows grid; the row lives inside it. */}
            <div className="ds-cols --nowrap --gap-xsmall">
              <input
                className="ds-input app-grow"
                value={newName}
                onChange={(event) => setNewName(event.target.value)}
                placeholder={
                  selected === null
                    ? t(`organisation.${kind}.namePlaceholder`)
                    : t("organisation.tree.addUnder", { name: selected.name })
                }
                maxLength={100}
                aria-label={t(`organisation.${kind}.newNameLabel`)}
              />
              <button
                type="submit"
                className="ds-button --primary --icon-plus"
                disabled={busy || newName.trim() === ""}
              >
                <span>{t("common.add")}</span>
              </button>
            </div>
          </form>
        )}

        {canManage && selectedId !== null && (
          <div>
            <button
              type="button"
              className="ds-button --plain --xsmall"
              onClick={() => onSelectedChange(null)}
            >
              <span>{t("organisation.tree.clearSelection")}</span>
            </button>
          </div>
        )}

        {error !== null && <Alert tone="failed">{error}</Alert>}

        {ready && (
          <HierarchyTree
            nodes={nodes}
            selectedId={selectedId}
            onSelect={onSelectedChange}
            emptyMessage={t(`organisation.${kind}.empty`)}
            label={t(`organisation.${kind}.heading`)}
            {...(operations.badge === undefined
              ? {}
              : { renderBadge: (node) => operations.badge?.(node.id) })}
            renderActions={(node) =>
              !canManage ? null : (
                <>
                  <button
                    type="button"
                    className="ds-button --plain --xsmall --hide-label --icon-edit"
                    onClick={() => setPending({ kind: "rename", node })}
                    disabled={busy}
                  >
                    <span>{t("common.rename")}</span>
                  </button>
                  <button
                    type="button"
                    className="ds-button --plain --xsmall --hide-label --icon-arrow-right-curved"
                    onClick={() => setPending({ kind: "move", node })}
                    disabled={busy}
                  >
                    <span>{t("common.move")}</span>
                  </button>
                  <button
                    type="button"
                    className="ds-button --plain --xsmall --hide-label --icon-trash --alert"
                    onClick={() => setPending({ kind: "remove", node })}
                    disabled={busy}
                  >
                    <span>{t("common.delete")}</span>
                  </button>
                </>
              )
            }
          />
        )}
      </section>

      <section className="ds-card">
        {selected === null ? (
          <p className="ds-text --muted app-empty">
            {t(`organisation.${kind}.selectPrompt`)}
          </p>
        ) : (
          children(selected)
        )}
      </section>

      {pending?.kind === "rename" && (
        <RenameDialog
          node={pending.node}
          kind={kind}
          onRename={(name) => void rename(pending.node, name)}
          onCancel={cancel}
        />
      )}
      {pending?.kind === "move" && (
        <MoveDialog
          node={pending.node}
          nodes={nodes}
          kind={kind}
          onMove={(parentId) => void move(pending.node, parentId)}
          onCancel={cancel}
        />
      )}
      {pending?.kind === "remove" && (
        <ConfirmDialog
          title={t("organisation.tree.deleteTitle", {
            name: pending.node.name,
          })}
          description={t("organisation.tree.deleteDescription")}
          confirmLabel={t("common.delete")}
          onConfirm={() => void remove(pending.node)}
          onCancel={cancel}
        />
      )}
    </div>
  );
}
